import { Box, Button, Chip, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { RESPONSE_LABELS, STATUS_LABELS } from "../../utils/coversApi";

dayjs.extend(relativeTime);

const toDate = (ts) => (ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null);
export const timeAgo = (ts) => {
  const d = toDate(ts);
  return d ? dayjs(d).fromNow() : "";
};

const statusColor = (s) => ({ open: "warning", taken: "success" }[s] || "default");
const responseColor = (r) => ({ accepted: "success", declined: "error" }[r] || "default");

/**
 * One-line summary of a cover request shown inside a session block, with an
 * expandable list of who has replied.
 */
const CoverRequestStrip = ({ group, expanded, onToggle, onManage, isManager, isDark }) => {
  const invited = (group.invitedTutorIds || []).map((id) => ({ id, ...(group.invited?.[id] || {}) }));
  const declined = invited.filter((i) => i.response === "declined");
  const accepted = invited.filter((i) => i.response === "accepted");
  const waiting = invited.filter((i) => i.response !== "declined" && i.response !== "accepted");
  const order = { accepted: 0, declined: 1, pending: 2 };
  const sorted = [...invited].sort(
    (a, b) => (order[a.response] ?? 2) - (order[b.response] ?? 2) || (a.name || "").localeCompare(b.name || "")
  );

  const allDeclined = group.status === "open" && invited.length > 0 && declined.length === invited.length;

  let summary;
  if (group.status === "taken") {
    summary = (
      <>
        Covered by <b>{group.acceptedByName}</b> · accepted {timeAgo(group.acceptedAt)}
      </>
    );
  } else if (allDeclined) {
    summary = (
      <>
        <b>Everyone has declined</b> · sent {timeAgo(group.createdAt)} to {invited.length} tutor
        {invited.length === 1 ? "" : "s"} · add more tutors or cancel
      </>
    );
  } else if (group.status === "open") {
    summary = (
      <>
        Sent {timeAgo(group.createdAt)} to {invited.length} tutor{invited.length === 1 ? "" : "s"} ·{" "}
        <b>{declined.length} declined</b> · {waiting.length} waiting
      </>
    );
  } else if (group.status === "cancelled") {
    summary = (
      <>
        Cancelled {timeAgo(group.cancelledAt || group.updatedAt)}
        {group.cancelledByName && ` by ${group.cancelledByName}`} · {declined.length} had declined
      </>
    );
  } else {
    summary = <>Expired {timeAgo(group.updatedAt)} · {declined.length} declined, {waiting.length} never replied</>;
  }

  const tone = allDeclined
    ? { bgcolor: isDark ? "rgba(239,83,80,0.12)" : "#FDECEA" }
    : group.status === "open"
      ? { bgcolor: isDark ? "rgba(249,199,79,0.08)" : "#FFF8E1" }
      : group.status === "taken"
        ? { bgcolor: isDark ? "rgba(102,187,106,0.08)" : "#E8F5E9" }
        : { bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#F3F3F3", opacity: 0.85 };

  return (
    <Box data-testid="cover-request-strip" data-status={group.status} sx={{ px: 2, py: 1, ...tone }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Chip
          size="small"
          color={allDeclined ? "error" : statusColor(group.status)}
          label={allDeclined ? "All declined" : STATUS_LABELS[group.status] || group.status}
        />
        <Typography variant="body2" flex={1} minWidth={200}>
          {summary}
        </Typography>
        {isManager && (
          <Button size="small" onClick={onManage}>
            Manage
          </Button>
        )}
        <Tooltip title={expanded ? "Hide replies" : "Show who has replied"}>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={expanded ? "Hide replies" : "Show replies"}
            aria-expanded={expanded}
            sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {group.status === "open" && invited.length > 0 && (
        <Tooltip
          title={`${declined.length} declined · ${waiting.length} waiting · ${invited.length} invited`}
          placement="bottom-start"
        >
          <Box
            data-testid="cover-progress"
            aria-label={`${declined.length} of ${invited.length} declined`}
            sx={{
              mt: 0.75,
              height: 6,
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                width: `${(declined.length / invited.length) * 100}%`,
                bgcolor: allDeclined ? "#D32F2F" : "#EF5350",
                transition: "width 300ms",
              }}
            />
          </Box>
        </Tooltip>
      )}
      <Collapse in={expanded} unmountOnExit>
        <Stack gap={0.5} mt={1} pl={0.5}>
          {group.message && (
            <Typography variant="caption" sx={{ opacity: 0.75, mb: 0.5 }}>
              Sent by {group.createdByName}: "{group.message}"
            </Typography>
          )}
          {sorted.map((i) => (
            <Stack key={i.id} direction="row" alignItems="center" gap={1}>
              <Chip
                size="small"
                variant={i.response === "pending" || !i.response ? "outlined" : "filled"}
                color={responseColor(i.response)}
                label={RESPONSE_LABELS[i.response] || "No reply"}
                sx={{ minWidth: 84 }}
              />
              <Typography variant="body2">{i.name || i.id}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {i.response === "pending" || !i.response
                  ? `invited ${timeAgo(i.invitedAt)}`
                  : `${i.response} ${timeAgo(i.respondedAt)}`}
              </Typography>
            </Stack>
          ))}
          {accepted.length === 0 && invited.length === 0 && (
            <Typography variant="caption">No tutors invited.</Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default CoverRequestStrip;
